import { vec3 } from 'gl-matrix';
import {
    ClipBudget,
    feretDiameter,
    InsideChord,
    longestInsideChord,
    longestPerpendicularChord,
    PlanarRingMm,
    SlicePart,
    spend,
} from './longestInsideChord';
import { DomainValidationError } from '~/domain/errors/DomainError';
import {
    closestPointOnSegment,
    PointMm,
    toPointMm,
    Vec2Mm,
} from '~/domain/geometry/patientGeometry';
import { StructureSdf } from '~/domain/structures/structureSdf';
import { extractSlicePolygons } from '~/engine/core/utils/marchingSquares';
import type { Grid } from '~/engine/data/common/Grid';
import { canonicalisePolygon } from '~/engine/util/algorithms/sdf/cross-section/crossSectionGeometry';
import {
    computeSignedArea,
    convexHull,
    groupRingsIntoParts,
} from '~/engine/util/algorithms/sdf/polygonWinding';
import { SDF_ISO_THRESHOLD } from '~/engine/util/algorithms/sdf/sdfConstants';

export interface AxialDiametersOptions {
    /** Lengths closer than this are a tie. Default 0.01 mm. */
    readonly tieToleranceMm?: number;
    /** Outline simplification tolerance as a fraction of the smaller in-plane spacing. Default 0.1. */
    readonly simplifyTolerancePx?: number;
    /** Outline vertices the search may visit before it gives up (about a second). Default 1e8. */
    readonly maxClipVertexVisits?: number;
}

export interface DiameterSegment {
    /** The ends in patient mm, on the centre plane of the measured slice. */
    readonly points: readonly [PointMm, PointMm];
    /** In-plane length in mm. */
    readonly lengthMm: number;
}

export interface AxialDiametersResult {
    readonly sliceIndex: number;
    readonly long: DiameterSegment;
    readonly short: DiameterSegment;
    /** Where the two segments cross, in patient mm. */
    readonly centreMm: PointMm;
    readonly diagnostics: { readonly slicesContoured: number };
}

/**
 * An upper bound in mm on any segment inside the structure on `slice`, 0 when
 * there is none: the largest Feret diameter of a group of touching inside
 * voxels, each voxel widened by half a voxel on every side.
 */
function sliceLengthBoundMm(
    slice: Uint8Array,
    { sizeX, sizeY, resX, resY }: Grid
): number {
    // Row runs of inside voxels as [first, last, row], each linked towards the
    // lowest run of its group.
    const runs: number[][] = [];
    const link: number[] = [];
    const rootOf = (run: number) => {
        while (link[run] !== run) {
            run = link[run] = link[link[run]];
        }
        return run;
    };
    let above: number[] = [];
    for (let y = 0; y < sizeY; y++) {
        const row = slice.subarray(y * sizeX, (y + 1) * sizeX);
        const runsOfRow: number[] = [];
        for (let x = 0; x < sizeX; x++) {
            if (row[x] < SDF_ISO_THRESHOLD) {
                continue;
            }
            const first = x;
            while (x + 1 < sizeX && row[x + 1] >= SDF_ISO_THRESHOLD) {
                x++;
            }
            const run = runs.push([first, x, y]) - 1;
            link.push(run);
            runsOfRow.push(run);
            // Runs touching diagonally are one group.
            for (const other of above) {
                if (runs[other][0] <= x + 1 && runs[other][1] >= first - 1) {
                    const [p, q] = [rootOf(other), rootOf(run)];
                    link[Math.max(p, q)] = Math.min(p, q);
                }
            }
        }
        above = runsOfRow;
    }
    // Voxel (x, y) has its centre at (x + 0.5, y + 0.5).
    const groups: Vec2Mm[][] = [];
    runs.forEach(([first, last, row], run) => {
        const [left, right] = [(first - 0.5) * resX, (last + 1.5) * resX];
        const [top, bottom] = [(row - 0.5) * resY, (row + 1.5) * resY];
        (groups[rootOf(run)] ??= []).push(
            [left, top],
            [right, top],
            [left, bottom],
            [right, bottom]
        );
    });
    return groups.reduce(
        (bound, corners) =>
            Math.max(bound, feretDiameter(convexHull(corners))?.lengthMm ?? 0),
        0
    );
}

/**
 * Douglas-Peucker on a closed ring, one-sided: the outline may shrink by
 * `toleranceMm` but grow by only a tenth of it, as a diameter grazing a hole
 * changes by many times the outline's shift.
 */
function simplifyClosedRing(
    ring: PlanarRingMm,
    toleranceMm: number,
    isHole: boolean
): PlanarRingMm {
    const count = ring.length;
    // Left of the direction of travel for a counter-clockwise outer ring and
    // for a clockwise hole.
    const structureOnLeft = computeSignedArea(ring) < 0 !== isHole;
    const [x0, y0] = ring[0];
    const distanceSq = ([x, y]: Vec2Mm) => (x - x0) ** 2 + (y - y0) ** 2;
    const farthest = ring.reduce(
        (best, point, i) =>
            distanceSq(point) > distanceSq(ring[best]) ? i : best,
        0
    );
    const keep = new Uint8Array(count);
    keep[0] = keep[farthest] = 1;
    // [first, last] stretches still to simplify; `count` stands for vertex 0.
    const spans = [
        [0, farthest],
        [farthest, count],
    ];
    for (let span = spans.pop(); span; span = spans.pop()) {
        const [first, last] = span;
        const [ax, ay] = ring[first];
        const dx = ring[last % count][0] - ax;
        const dy = ring[last % count][1] - ay;
        const lengthSq = dx * dx + dy * dy;
        let worst = -1;
        let worstExcess = 0;
        for (let i = first + 1; i < last; i++) {
            const px = ring[i][0] - ax;
            const py = ring[i][1] - ay;
            const side = dx * py - dy * px;
            const t = Math.min(1, Math.max(0, (px * dx + py * dy) / lengthSq));
            // Dropping a vertex on the structure's side of the chord would grow
            // the outline.
            const allowance =
                side !== 0 && side > 0 === structureOnLeft
                    ? toleranceMm * 0.1
                    : toleranceMm;
            const excess = Math.hypot(px - t * dx, py - t * dy) - allowance;
            if (excess > worstExcess) {
                worstExcess = excess;
                worst = i;
            }
        }
        if (worst >= 0) {
            keep[worst] = 1;
            spans.push([first, worst], [worst, last]);
        }
    }
    const simplified = ring.filter((_, i) => keep[i]);
    return simplified.length >= 3 ? simplified : ring;
}

/**
 * The structure's simplified outline on one slice, as parts in in-plane mm.
 * Throws {@link OutlineTooComplexError} once `budget` is spent.
 */
export function sliceOutlineParts(
    slice: Uint8Array,
    sdf: StructureSdf,
    simplifyToleranceMm: number,
    budget: ClipBudget
): SlicePart[] {
    const { sizeX, sizeY, resX, resY } = sdf.grid;
    const rings = extractSlicePolygons(slice, sizeX, sizeY).map((polygon) =>
        canonicalisePolygon(
            polygon.map(([x, y]): Vec2Mm => [x * resX, y * resY])
        )
    );
    // Nesting tests one vertex of each ring against every ring.
    spend(budget, rings.length * rings.reduce((n, ring) => n + ring.length, 0));
    // Nested before simplifying: outlines never cross, simplified ones may.
    return groupRingsIntoParts(rings).map((part) =>
        part.map((ring, i) =>
            simplifyClosedRing(ring, simplifyToleranceMm, i > 0)
        )
    );
}

/**
 * The long diameter of a structure, the longest segment inside it on any
 * transversal slice, and the short diameter, the longest segment inside it on
 * that slice perpendicular to the long one and crossing it; null when nothing
 * can be measured. Holes are outside and separate parts are never bridged. Ties
 * go to the longer short diameter, then to the slice nearest the middle of the
 * structure. Throws on an invalid option or slice size, and {@link
 * OutlineTooComplexError} when `maxClipVertexVisits` is exceeded.
 */
export function findAxialDiameters(
    sdf: StructureSdf,
    options: AxialDiametersOptions = {}
): AxialDiametersResult | null {
    const {
        tieToleranceMm = 0.01,
        simplifyTolerancePx = 0.1,
        maxClipVertexVisits = 1e8,
    } = options;
    const { sizeX, sizeY, resX, resY } = sdf.grid;
    for (const [name, value] of Object.entries({
        tieToleranceMm,
        simplifyTolerancePx,
        maxClipVertexVisits,
        resX,
        resY,
    })) {
        if (!(value > 0) || !Number.isFinite(value)) {
            throw new DomainValidationError(
                `${name} must be a positive finite number.`
            );
        }
    }
    const simplifyToleranceMm = simplifyTolerancePx * Math.min(resX, resY);
    // Inside stretches of one line closer than this are one stretch.
    const mergeGapMm = 1e-9 * Math.max(sizeX * resX, sizeY * resY);
    const budget = { vertexVisits: maxClipVertexVisits };

    const boundsMm = sdf.pixels.slice(0, sdf.grid.sizeZ).map((slice, z) => {
        if (slice.length !== sizeX * sizeY) {
            throw new DomainValidationError(
                `Slice ${z} of the structure field holds ${slice.length} values; a ${sizeX} x ${sizeY} slice holds ${sizeX * sizeY}.`
            );
        }
        return sliceLengthBoundMm(slice, sdf.grid);
    });
    const occupied = [...boundsMm.keys()].filter((z) => boundsMm[z] > 0);
    const meanSlice = occupied.reduce((sum, z) => sum + z, 0) / occupied.length;

    const candidates: {
        sliceIndex: number;
        part: SlicePart;
        long: InsideChord;
    }[] = [];
    let longestMm = -Infinity;
    let slicesContoured = 0;
    // Longest bound first: once one falls below the tie band, the rest do too.
    for (const sliceIndex of occupied.toSorted(
        (p, q) => boundsMm[q] - boundsMm[p] || p - q
    )) {
        if (boundsMm[sliceIndex] < longestMm - tieToleranceMm) {
            break;
        }
        slicesContoured++;
        for (const part of sliceOutlineParts(
            sdf.pixels[sliceIndex],
            sdf,
            simplifyToleranceMm,
            budget
        )) {
            const long = longestInsideChord(
                part,
                longestMm - tieToleranceMm,
                mergeGapMm,
                budget
            );
            if (long) {
                candidates.push({ sliceIndex, part, long });
                longestMm = Math.max(longestMm, long.lengthMm);
            }
        }
    }

    const measured = candidates
        .filter(({ long }) => long.lengthMm >= longestMm - tieToleranceMm)
        .flatMap((candidate) => {
            const short = longestPerpendicularChord(
                candidate.part,
                candidate.long,
                mergeGapMm,
                budget
            );
            return short ? [{ ...candidate, short }] : [];
        });
    const widestMm = Math.max(...measured.map(({ short }) => short.lengthMm));
    // Stable: parts of one slice stay in their order.
    const [chosen] = measured
        .filter(({ short }) => short.lengthMm >= widestMm - tieToleranceMm)
        .sort(
            (a, b) =>
                Math.abs(a.sliceIndex - meanSlice) -
                    Math.abs(b.sliceIndex - meanSlice) ||
                a.sliceIndex - b.sliceIndex ||
                b.long.lengthMm - a.long.lengthMm ||
                b.short.lengthMm - a.short.lengthMm
        );
    if (!chosen) {
        return null;
    }

    const { sliceIndex, long, short } = chosen;
    // A plain array keeps double precision, so the points stay on one plane.
    const toMm = ([x, y]: Vec2Mm) =>
        toPointMm(
            vec3.transformMat4(
                [0, 0, 0],
                [x / resX, y / resY, sliceIndex + 0.5],
                sdf.grid.voxelToWorld
            )
        );
    const segmentOf = ({ a, b, lengthMm }: InsideChord): DiameterSegment => ({
        points: [toMm(a), toMm(b)],
        lengthMm,
    });
    return {
        sliceIndex,
        long: segmentOf(long),
        short: segmentOf(short),
        // The short segment crosses the long one: its foot on it.
        centreMm: toMm(closestPointOnSegment(short.a, long.a, long.b)),
        diagnostics: { slicesContoured },
    };
}
